import { ClockCircleOutlined, FireOutlined, WarningOutlined } from "@ant-design/icons";
import { Flex } from "antd";
import type { CSSProperties, ReactNode } from "react";
import type { FindingSeverity, FindingStatus } from "../../service/compliance.service";

// ─── Types ───────────────────────────────────────────────────────────────────

// Single source of truth lives in compliance.service.ts (matches the backend);
// re-exported here under the names the compliance UI already uses.
export type Severity = FindingSeverity;
export type Status = FindingStatus;

// ─── Visual config ───────────────────────────────────────────────────────────

// Single source of truth for every semantic color in the compliance area.
// All banners, tags, table cells, trend arrows, and capture bars pull from
// here, so the same meaning always reads the same color instead of each file
// hand-picking its own near-duplicate red/amber/green.
//   fg     = text       bg     = fill
//   border = hairline   accent = icon / dot / chart fill
export type Tone = "danger" | "warning" | "success" | "info" | "neutral";

export const TONE: Record<Tone, { fg: string; bg: string; border: string; accent: string }> = {
  danger: { fg: "#b91c1c", bg: "#fef2f2", border: "#fecaca", accent: "#dc2626" },
  warning: { fg: "#b45309", bg: "#fffbeb", border: "#fde68a", accent: "#d97706" },
  success: { fg: "#047857", bg: "#ecfdf5", border: "#a7f3d0", accent: "#10b981" },
  info: { fg: "#1d4ed8", bg: "#eff6ff", border: "#bfdbfe", accent: "#3b82f6" },
  neutral: { fg: "#374151", bg: "#f9fafb", border: "#e5e7eb", accent: "#9ca3af" },
};

// Severity is a 4-step scale (critical → low). Critical/medium/low reuse the
// danger/warning/neutral tones; high keeps a distinct orange step in between.
export const SEVERITY_META: Record<
  Severity,
  { label: string; color: string; bg: string; border: string; icon: ReactNode }
> = {
  critical: {
    label: "Critical",
    color: TONE.danger.fg,
    bg: TONE.danger.bg,
    border: TONE.danger.border,
    icon: <FireOutlined />,
  },
  high: { label: "High", color: "#c2410c", bg: "#fff7ed", border: "#fed7aa", icon: <WarningOutlined /> },
  medium: {
    label: "Medium",
    color: TONE.warning.fg,
    bg: TONE.warning.bg,
    border: TONE.warning.border,
    icon: <WarningOutlined />,
  },
  low: {
    label: "Low",
    color: TONE.neutral.fg,
    bg: "#f3f4f6",
    border: TONE.neutral.border,
    icon: <ClockCircleOutlined />,
  },
};

export const STATUS_META: Record<Status, { label: string; color: string; bg: string; dot: string }> = {
  open: { label: "Open", color: TONE.danger.fg, bg: TONE.danger.bg, dot: TONE.danger.accent },
  in_review: { label: "In review", color: TONE.info.fg, bg: TONE.info.bg, dot: TONE.info.accent },
  addressed: { label: "Addressed", color: TONE.warning.fg, bg: TONE.warning.bg, dot: TONE.warning.accent },
  closed: { label: "Closed", color: TONE.success.fg, bg: TONE.success.bg, dot: TONE.success.accent },
  false_positive: { label: "False positive", color: TONE.neutral.fg, bg: TONE.neutral.bg, dot: TONE.neutral.accent },
};

export const SLA_DAYS: Record<Severity, number> = { critical: 1, high: 3, medium: 7, low: 14 };

export const SEVERITY_ORDER: Severity[] = ["critical", "high", "medium", "low"];

// ─── Helpers ─────────────────────────────────────────────────────────────────

export const ageDays = (isoDate: string) =>
  Math.max(0, Math.floor((Date.now() - new Date(isoDate).getTime()) / 86400000));

export const ageLabel = (d: number) => {
  if (d < 1) return "Today";
  if (d === 1) return "1 day";
  if (d < 7) return `${d} days`;
  if (d < 14) return "1 week";
  if (d < 30) return `${Math.floor(d / 7)} weeks`;
  return `${Math.floor(d / 30)}mo`;
};

// ─── Tag components ──────────────────────────────────────────────────────────

export const SeverityTag = ({ severity }: { severity: Severity }) => {
  const m = SEVERITY_META[severity];
  return (
    <Flex
      align="center"
      gap={4}
      style={{
        background: m.bg,
        color: m.color,
        border: `1px solid ${m.border}`,
        padding: "2px 8px",
        borderRadius: 6,
        fontSize: 11,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        width: "fit-content",
      }}
    >
      {m.icon}
      <span>{m.label}</span>
    </Flex>
  );
};

export const StatusTag = ({ status }: { status: Status }) => {
  const m = STATUS_META[status];
  return (
    <Flex
      align="center"
      gap={5}
      style={{
        background: m.bg,
        color: m.color,
        padding: "3px 9px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        width: "fit-content",
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: 999, background: m.dot, display: "inline-block" }} />
      {m.label}
    </Flex>
  );
};

// Standard inline alert banner for the compliance area (critical findings,
// warnings, etc.). Colors come from TONE so every banner — whatever the tone —
// shares the same text/fill/border/accent treatment.
export const ComplianceBanner = ({
  tone,
  icon,
  action,
  children,
  style,
}: {
  tone: Tone;
  icon?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  style?: CSSProperties;
}) => {
  const t = TONE[tone];
  return (
    <Flex
      align="center"
      gap={10}
      wrap="wrap"
      style={{
        background: t.bg,
        border: `1px solid ${t.border}`,
        borderRadius: 8,
        padding: "12px 16px",
        ...style,
      }}
    >
      {icon ? <span style={{ color: t.accent, fontSize: 18, display: "inline-flex" }}>{icon}</span> : null}
      <span style={{ flex: "1 1 200px", color: t.fg, fontSize: 13 }}>{children}</span>
      {action ? <span style={{ flexShrink: 0 }}>{action}</span> : null}
    </Flex>
  );
};
